/**
 * Banner shown at the top of the patient treatment-plan detail page.
 *
 * Two render variants:
 *
 *  - **Active** (purple-tinted, animated shimmer + pulsing dot): one or
 *    more in-flight scrape jobs for this patient. Shows progress per
 *    operation, with a 2FA attention pill when applicable.
 *
 *  - **Attention** (red-tinted, no shimmer): the patient has at least
 *    one of: a recent failed scrape job for them, or a manually-set
 *    `manual_attention_note`. The card renders both sections when both
 *    exist — manual note on top, failed-scrape section (with Retry)
 *    below. When only the manual note exists, the banner renders
 *    without a Retry button.
 *
 * The banner hides when neither condition holds. Tag membership no
 * longer gates the banner — notes are patient-scoped now and surface
 * alongside any tag (or none).
 *
 * react-redux 6.x — uses connect().
 */

import React from "react";
import { connect } from "react-redux";
import { Button, message } from "antd";
import { ExclamationCircleFilled, FlagFilled, KeyOutlined, LoadingOutlined, ReloadOutlined } from "@ant-design/icons";

import {
  selectActiveJobsForPatient,
  selectLatestFailedJobForPatient,
} from "../../reducers/scrapeJobs";
import { triggerAIFetch } from "../TreatmentPlans/lib/aiFetch";
import { armNextDispatchId, newDispatchId } from "../../utils/dispatchTracking";
import { debugLog } from "../../utils/debugLog";
import { MISSING_INFO_TAG_NAME } from "../InsuranceRequests/requestTypes";
import "./ScrapeJobsIndicator.css";

function jobLabel(job) {
  const op = (job.operation_type || "").replace(/_/g, " ");
  if (!op) return "Insurance fetch";
  return op.charAt(0).toUpperCase() + op.slice(1);
}

function carrierName(job) {
  if (!job || !job.carrier_code) return "";
  const c = String(job.carrier_code).toLowerCase();
  return c.charAt(0).toUpperCase() + c.slice(1);
}

function statusLabel(state) {
  switch (state) {
    case "queued":       return "Queued";
    case "claimed":      return "Starting";
    case "running":      return "Running";
    case "awaiting_2fa": return "2FA needed";
    default:             return state;
  }
}

function formatRelative(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diff = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (diff < 5) return "just now";
  if (diff < 60) return diff + "s ago";
  if (diff < 3600) return Math.floor(diff / 60) + "m ago";
  if (diff < 86400) return Math.floor(diff / 3600) + "h ago";
  return Math.floor(diff / 86400) + "d ago";
}

// Patient carries the "Details Needed by Smilepass" tag? Switches the banner from
// the neutral note treatment to the red attention treatment. Defensive
// against either string-or-{name:string} shape.
function hasNeedsAttentionTag(patientTags) {
  if (!patientTags || !patientTags.length) return false;
  for (let i = 0; i < patientTags.length; i++) {
    const v = patientTags[i];
    const name = typeof v === "string" ? v : v && v.name;
    if (name === MISSING_INFO_TAG_NAME) return true;
  }
  return false;
}

let _lastBannerLogged = "";

class ScrapeJobsBannerInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = { isRetrying: false };
  }

  handleRetry = async () => {
    const failed = this.props.failedJob;
    if (!failed || !failed.policy_id) {
      message.error("This run isn't tied to a specific policy — open the breakdown modal to start a new fetch.");
      return;
    }
    const dispatchId = newDispatchId();
    armNextDispatchId(dispatchId);
    debugLog.info("button", "scrape_button_click", {
      dispatch_id: dispatchId,
      patient_id: this.props.patientId,
      policy_id: failed.policy_id,
      reason: "banner_retry_after_failure",
    });
    try {
      this.setState({ isRetrying: true });
      const res = await triggerAIFetch(failed.policy_id, {});
      debugLog.info("button", "scrape_button_dispatch_response", {
        dispatch_id: dispatchId,
        http_status: res && res.ok ? 200 : 0,
        reason: res && res.ok ? "ok" : (res && res.error) || "unknown",
      });
      if (res.ok) {
        message.success("Retrying — the previous failure will clear when this run finishes.");
      } else {
        message.error(res.error || "Couldn't restart the fetch. Please try again.");
      }
    } finally {
      this.setState({ isRetrying: false });
    }
  };

  renderActive(jobs) {
    const awaitingCount = jobs.filter((j) => j.state === "awaiting_2fa").length;
    const titleNoun =
      jobs.length === 1 ? "Insurance fetch in progress" : "Insurance fetches in progress";

    return (
      <div className="scrape-banner" role="status" aria-live="polite">
        <div className="scrape-banner-header">
          <span className="scrape-banner-pulse" aria-hidden="true">
            <span className="scrape-banner-pulse-ring" />
            <span className="scrape-banner-pulse-core" />
          </span>
          <span className="scrape-banner-title">
            {titleNoun}
            {jobs.length > 1 ? (
              <span className="scrape-banner-title-count"> · {jobs.length}</span>
            ) : null}
          </span>
          {awaitingCount > 0 ? (
            <span className="scrape-banner-attention">
              <KeyOutlined /> {awaitingCount} waiting for code
            </span>
          ) : null}
        </div>

        {jobs.map((job) => {
          const is2fa = job.state === "awaiting_2fa";
          const rowClass =
            "scrape-banner-row" + (is2fa ? " scrape-banner-row-2fa" : "");
          const carrier = carrierName(job);
          return (
            <div key={job.job_id} className={rowClass}>
              <span className="scrape-banner-row-icon" aria-hidden="true">
                {is2fa ? <KeyOutlined /> : <LoadingOutlined spin />}
              </span>
              <span className="scrape-banner-row-label">{jobLabel(job)}</span>
              <span className="scrape-banner-row-meta">
                {carrier ? "· " + carrier : ""}
                {job.progress_label ? " · " + job.progress_label : ""}
              </span>
              <span className="scrape-banner-row-status">{statusLabel(job.state)}</span>
            </div>
          );
        })}
      </div>
    );
  }

  renderManualNoteHeader() {
    const { manualAttentionNote, manualAttentionNoteSetAt, patientTags } = this.props;
    if (!manualAttentionNote) return null;
    const when = formatRelative(manualAttentionNoteSetAt);
    const isAttention = hasNeedsAttentionTag(patientTags);
    // Red flag + "Details Needed by Smilepass" when the patient is explicitly
    // tagged Details Needed by Smilepass; otherwise neutral purple + "Note".
    const iconClass = isAttention
      ? "scrape-banner-failed-icon"
      : "scrape-banner-note-icon";
    const title = isAttention ? "Details Needed by Smilepass" : "Note";
    return (
      <div className="scrape-banner-header">
        <span className={iconClass} aria-hidden="true">
          <FlagFilled />
        </span>
        <span className="scrape-banner-title">
          {title}
          {when ? <span className="scrape-banner-failed-time"> · {when}</span> : null}
        </span>
      </div>
    );
  }

  renderFailedHeader(failed) {
    const carrier = carrierName(failed);
    const when = formatRelative(failed.completed_at || failed.updated_at);
    return (
      <div className="scrape-banner-header">
        <span className="scrape-banner-failed-icon" aria-hidden="true">
          <ExclamationCircleFilled />
        </span>
        <span className="scrape-banner-title">
          Last {jobLabel(failed).toLowerCase()} fetch failed
          {carrier ? <span className="scrape-banner-title-count"> · {carrier}</span> : null}
          {when ? <span className="scrape-banner-failed-time"> · {when}</span> : null}
        </span>
        {failed.policy_id ? (
          <Button
            type="primary"
            size="small"
            icon={<ReloadOutlined />}
            loading={this.state.isRetrying}
            onClick={this.handleRetry}
            className="scrape-banner-retry-btn"
          >
            Retry
          </Button>
        ) : null}
      </div>
    );
  }

  renderAttention(failed) {
    // Renders the manual-note section (if a note exists) on top, the
    // failed-scrape section (if a failed job exists) on the bottom, with
    // a faint divider between them when both are present. At least one
    // must exist for this method to be called.
    const { manualAttentionNote, patientTags } = this.props;
    const showFailed = !!failed;
    const showBoth = !!manualAttentionNote && showFailed;
    // Red treatment when there's an actual escalation signal — a failed
    // scrape, or the patient is tagged Details Needed by Smilepass. A plain note on
    // its own gets the neutral purple variant so it doesn't read as an
    // alarm. The body chip class follows the same rule.
    const isAlarming = showFailed || hasNeedsAttentionTag(patientTags);
    const variantClass = isAlarming ? "scrape-banner-failed" : "scrape-banner-note";
    const noteBodyClass = isAlarming
      ? "scrape-banner-failed-body"
      : "scrape-banner-note-body";
    const dividerColor = isAlarming
      ? "rgba(196, 29, 42, 0.18)"
      : "rgba(107, 67, 181, 0.18)";
    return (
      <div className={`scrape-banner ${variantClass}`} role="status" aria-live="polite">
        {manualAttentionNote ? (
          <React.Fragment>
            {this.renderManualNoteHeader()}
            <div
              className={noteBodyClass}
              style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
            >
              {manualAttentionNote}
            </div>
          </React.Fragment>
        ) : null}
        {showBoth ? (
          <div
            aria-hidden="true"
            style={{
              height: 1,
              background: dividerColor,
              margin: "12px 0",
            }}
          />
        ) : null}
        {showFailed ? (
          <React.Fragment>
            {this.renderFailedHeader(failed)}
            <div className="scrape-banner-failed-body">
              {failed.error_message || "The latest fetch failed but no message was returned."}
            </div>
          </React.Fragment>
        ) : null}
      </div>
    );
  }

  render() {
    const { jobs, failedJob, patientId, manualAttentionNote } = this.props;
    const hasActive = jobs && jobs.length > 0;
    // Notes are captured for any tag now, so the attention banner shows
    // whenever there's either a failed scrape or a manual note —
    // independent of which tag(s) the patient currently carries.
    const showAttention = !hasActive && (!!failedJob || !!manualAttentionNote);

    // Lightweight tracer: log once per (patient, mode, count) so we can
    // confirm in scrape_flow.log which variant rendered.
    const fingerprint =
      patientId + ":" +
      (hasActive ? "active:" + jobs.length + ":" + jobs.map((j) => j.state).join(",")
       : showAttention ? "attention:" + (failedJob ? failedJob.job_id : "none") + ":" + (manualAttentionNote ? "note" : "no-note")
       : "none");
    if (fingerprint !== _lastBannerLogged) {
      _lastBannerLogged = fingerprint;
      if (hasActive) {
        debugLog.info("banner", "banner_shown", {
          patient_id: patientId,
          active_count: jobs.length,
          states: jobs.map((j) => j.state),
        });
      } else if (showAttention) {
        debugLog.info("banner", "banner_attention_shown", {
          patient_id: patientId,
          job_id: failedJob ? failedJob.job_id : null,
          state: failedJob ? failedJob.state : null,
          has_manual_note: !!manualAttentionNote,
        });
      }
    }

    if (hasActive) return this.renderActive(jobs);
    if (showAttention) return this.renderAttention(failedJob);
    return null;
  }
}

const mapStateToProps = (state, ownProps) => ({
  jobs: selectActiveJobsForPatient(state, ownProps.patientId),
  failedJob: selectLatestFailedJobForPatient(state, ownProps.patientId),
  patientId: ownProps.patientId,
  patientTags: ownProps.patientTags,
  manualAttentionNote: ownProps.manualAttentionNote,
  manualAttentionNoteSetAt: ownProps.manualAttentionNoteSetAt,
});

export default connect(mapStateToProps)(ScrapeJobsBannerInner);
